import { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import {
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  useDataProvider,
  useLocalization,
} from '@ui-framework/index';
import { nanoid } from 'nanoid';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';
import {
  IconLibrary,
  StackablePageButtons,
  PageSection,
} from '@product-framework/index';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { GenerateApiKeyDialog } from '@product-framework/components/Dialogs';
import {
  APIKey,
  isActive,
  createdDateTime,
  expiresAt,
  description,
  lastModifiedDateTime,
  buttonActions,
  lastModifiedBy,
  UserRole,
} from './accessorFunctions';
import listSchema from './list.json';
import RevokeApiKeyModal from './RevokeApiKeyModal';
import {
  useServiceProvider,
  withServiceProvider,
} from '../../../../../providers/ServiceProviderProvider';
import getSPLevelStackableButtons from '../../../utils/getSPLevelStackableButtons';
import ServiceProviderLayout from '../../../components/ServiceProviderLayout';

function APIKeySettings() {
  const navigate = useNavigate();
  const { serviceProviderId } = useServiceProvider();
  const { formatMessage, messages } = useLocalization();
  const [open, setOpen] = useState(false);
  const [revokeObj, setRevokeObj] = useState(null);
  const [stackAblePageButtons, setStackAblePageButtons] = useState([]);
  const [listId, setListId] = useState(nanoid());

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const dataProvider = useDataProvider(provider);
  const listApiUrl = `${dataProvider.getApiUrl()}/sxcl/v1/service-providers/${serviceProviderId}/api-key`;

  const createButton = {
    label: formatMessage({
      id: 'CREATE_KEY',
      defaultMessage: 'Create Key',
    }),
    Icon: IconLibrary.ApiKey,
    permissionName: 'CAN_CREATE_SP_API_KEY',
    variant: 'outlined',
    onClick: () => {
      setOpen(true);
    },
  };

  const openRevokeModal = apikeyObj => {
    setRevokeObj(apikeyObj);
  };

  const listProps = {
    columnPrimaryId: 'id',
    storeName: 'ServiceProviderAPIKeySettings',
    dataProvider,
    schema: listSchema,
    initialState: {
      showColumnFilters: false,
    },
    downloadApiUrl: null,
    listApiUrl,
    accessorFunctions: {
      APIKey,
      UserRole,
      isActive,
      createdDateTime,
      expiresAt,
      description,
      lastModifiedDateTime,
      buttonActions,
      lastModifiedBy,
    },
    accessorFunctionsAdditional: {
      openRevokeModal,
    },
    sortBy: 'createdDateTime',
    sortOrder: 'ASC',
  };

  useEffect(() => {
    const buttons = [];
    buttons.push(createButton);
    setStackAblePageButtons(buttons);
  }, [messages]);

  return (
    <ServiceProviderLayout
      selectedStepperId="SYSTEM_SETTINGS_API_KEY"
      stackablePageButtons={getSPLevelStackableButtons({
        navigate,
      })}>
      <PageSection
        label={
          <FormattedMessage
            id="SYSTEM_SETTINGS_API_KEYS"
            defaultMessage="System Settings - Api Keys"
          />
        }
        HeaderContent={
          <StackablePageButtons
            backButton={false}
            buttons={stackAblePageButtons}
          />
        }>
        <Grid2 container direction="column" spacing={2}>
          <Box>
            <CRUDComponent
              key={listId}
              type={CRUD_COMPONENT_TYPE.ADVANCED_LIST}
              listProps={listProps}
            />
          </Box>
        </Grid2>
        <GenerateApiKeyDialog
          open={open}
          serviceProviderId={serviceProviderId}
          onClose={() => setOpen(false)}
          reRenderList={() => {
            setListId(nanoid());
          }}
        />
        {revokeObj && (
          <RevokeApiKeyModal
            record={revokeObj}
            close={() => {
              setRevokeObj(null);
              setListId(nanoid());
            }}
            serviceProviderId={serviceProviderId}
          />
        )}
      </PageSection>
    </ServiceProviderLayout>
  );
}

const APIKeySettingsObserver = observer(APIKeySettings);
export default withServiceProvider(APIKeySettingsObserver);
